import React, { createContext, useContext, useState, useEffect } from 'react';
import { languages, defaultLanguage, detectBrowserLanguage, detectLanguageByCountry } from '../i18n';

const LanguageContext = createContext();

// Verificar se está no browser (necessário para react-snap/SSR)
const isBrowser = typeof window !== 'undefined';

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Se não estiver no browser (react-snap/Puppeteer), usar idioma padrão
    if (!isBrowser) {
      return defaultLanguage;
    }
    
    // Tenta obter o idioma salvo no localStorage
    const savedLanguage = localStorage.getItem('personalNutriLanguage');
    if (savedLanguage && languages[savedLanguage]) {
      return savedLanguage;
    }
    
    // Se não houver idioma salvo, usar fallback
    return defaultLanguage;
  });

  // Detectar idioma baseado no país na inicialização
  useEffect(() => {
    // Só executar no browser
    if (!isBrowser) return;
    
    const detectLanguage = async () => {
      try {
        const detectedLanguage = await detectLanguageByCountry();
        setCurrentLanguage(detectedLanguage);
        localStorage.setItem('personalNutriLanguage', detectedLanguage);
      } catch (error) {
        console.warn('Erro na detecção automática de idioma:', error);
        // Manter o idioma atual se houver erro
      }
    };

    // Só detectar se não houver idioma salvo
    const savedLanguage = localStorage.getItem('personalNutriLanguage');
    if (!savedLanguage) {
      detectLanguage();
    }
  }, []);

  // Salva o idioma no localStorage quando muda e atualiza o atributo lang do HTML
  useEffect(() => {
    // Só executar no browser
    if (!isBrowser) return;
    
    localStorage.setItem('personalNutriLanguage', currentLanguage);
    // Atualizar atributo lang do HTML
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  const changeLanguage = (languageCode) => {
    if (languages[languageCode]) {
      setCurrentLanguage(languageCode);
    } else {
      console.warn(`Language ${languageCode} not supported`);
    }
  };

  const getCurrentLanguageInfo = () => {
    return {
      code: currentLanguage,
      name: languages[currentLanguage].name,
      flag: languages[currentLanguage].flag,
      translations: languages[currentLanguage].translations
    };
  };

  const value = {
    currentLanguage,
    changeLanguage,
    getCurrentLanguageInfo,
    availableLanguages: Object.keys(languages).map(code => ({
      code,
      name: languages[code].name,
      flag: languages[code].flag
    }))
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
